import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [requiresProfileSetup, setRequiresProfileSetup] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          // Validate token first
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Date.now() / 1000;
          
          // Check if token is expired
          if (tokenPayload.exp && tokenPayload.exp < currentTime) {
            console.log('Token expired, clearing auth state');
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            setRequiresProfileSetup(false);
            setLoading(false);
            return;
          }
          
          const response = await authService.getProfile();
          setUser({
            ...response.user,
            profile: response.profile || null
          });
          setRequiresProfileSetup(
            Boolean(response.requiresProfileSetup) ||
            (response.user?.userType === 'pharmacy' && !response.profile)
          );
        } catch (error) {
          console.error('Auth initialization error:', error);
          
          // Fallback only if the profile request truly fails (not pharmacy setup)
          if (error.response?.status === 404 && error.response?.data?.message === 'Profile not found') {
            try {
              const tokenPayload = JSON.parse(atob(token.split('.')[1]));
              if (tokenPayload.userType === 'pharmacy') {
                setUser({
                  id: tokenPayload.userId,
                  userType: tokenPayload.userType,
                  email: tokenPayload.email || null,
                  username: tokenPayload.username || null
                });
                setRequiresProfileSetup(true);
                setLoading(false);
                return;
              }
              // Admin has no profile in backend (or we now return 200); keep them logged in from token
              if (tokenPayload.userType === 'admin') {
                setUser({
                  id: tokenPayload.userId,
                  userType: 'admin',
                  email: tokenPayload.email || 'admin@pdapp.com'
                });
                setLoading(false);
                return;
              }
            } catch (tokenError) {
              console.error('Token parsing error:', tokenError);
            }
          }
          
          // For unexpected errors, keep the session if we already have a usable user.
          // Only clear auth on hard auth failures.
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const { token: newToken, user: userData } = response;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);

      // Load full profile so phone/address are available in the UI
      let fullUser = userData;
      try {
        const profileResponse = await authService.getProfile();
        fullUser = {
          ...userData,
          ...profileResponse.user,
          profile: profileResponse.profile || null
        };

        const needsSetup =
          Boolean(profileResponse.requiresProfileSetup) ||
          (fullUser.userType === 'pharmacy' && !profileResponse.profile);

        setUser(fullUser);
        setRequiresProfileSetup(needsSetup);
        return {
          success: true,
          requiresProfileSetup: needsSetup
        };
      } catch (profileError) {
        if (userData.userType === 'pharmacy' && profileError.response?.status === 404) {
          setUser(userData);
          setRequiresProfileSetup(true);
          return {
            success: true,
            requiresProfileSetup: true
          };
        }
      }

      setUser(fullUser);
      setRequiresProfileSetup(false);

      return {
        success: true,
        requiresProfileSetup: false
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      const { token: newToken, user: newUser, requiresProfileSetup: needsSetup } = response;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
      setRequiresProfileSetup(needsSetup || false);
      
      return { 
        success: true, 
        requiresProfileSetup: needsSetup || false 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    // Nuclear option - clear everything immediately
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear any cached data
    if (window.caches) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Force immediate redirect (no timeout needed)
    window.location.replace('/#/login');
  };

  const updateProfile = async (profileData) => {
    try {
      const updatedProfile = await authService.updateProfile(profileData);
      setUser(prev => ({
        ...prev,
        firstName: updatedProfile.firstName ?? prev?.firstName,
        lastName: updatedProfile.lastName ?? prev?.lastName,
        email: updatedProfile.email ?? prev?.email,
        username: updatedProfile.username ?? prev?.username,
        profile: updatedProfile
      }));
      return { success: true, profile: updatedProfile };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Profile update failed' 
      };
    }
  };

  const refreshProfile = async () => {
    try {
      const response = await authService.getProfile();
      if (response?.user || response?.profile) {
        setUser((prev) => ({
          ...(response.user || prev),
          profile: response.profile || response.user?.profile || prev?.profile
        }));
      }
      return { success: true, profile: response?.profile };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to refresh profile' };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user,
    userType: user?.userType,
    requiresProfileSetup,
    setRequiresProfileSetup
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
